import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Copy, Mail, MessageCircle, Printer } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { studentService, type ParentCode } from '@/services/student.service'

const APP_URL = 'https://app.myappacademia.com'

export const parentSignupLink = (code: string) => `${APP_URL}/onboard/individual?role=parent&code=${code}`

interface ParentAccessPanelProps {
  studentId: string
  /** parent e-mail already linked (e.g. given at enrolment: invited if new) */
  linkedEmail?: string
}

/** How a pupil's parents get into their parent space: a code to hand over
 * (paper, WhatsApp) and/or an invitation by e-mail. */
export function ParentAccessPanel({ studentId, linkedEmail }: ParentAccessPanelProps) {
  const { t, i18n } = useTranslation()
  const { toast } = useToast()
  const [code, setCode] = useState<ParentCode | null>(null)
  const [failed, setFailed] = useState(false)
  const [email, setEmail] = useState('')
  const [sending, setSending] = useState(false)
  const [invited, setInvited] = useState<string[]>([])

  useEffect(() => {
    let live = true
    setFailed(false)
    studentService.getParentCode(studentId)
      .then(c => { if (live) setCode(c) })
      .catch(() => { if (live) setFailed(true) })
    return () => { live = false }
  }, [studentId])

  const message = code
    ? t('parentAccess.shareMessage', {
        child: code.child_name, school: code.school_name, code: code.code, link: parentSignupLink(code.code),
        defaultValue: '{{school}}: follow {{child}} on AppAcademia. Sign up as a parent with the code {{code}}: {{link}}',
      })
    : ''

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message)
      toast({ title: t('parentAccess.copied', 'Copied') })
    } catch {
      toast({ title: t('parentAccess.copyFailed', 'Could not copy'), variant: 'destructive' })
    }
  }

  const print = () => {
    if (!code) return
    const w = window.open('', '_blank', 'width=480,height=640')
    if (!w) return
    const esc = (v: string) => v.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!))
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(code.child_name)}</title>
      <style>body{font-family:system-ui,sans-serif;padding:32px;color:#121B2E}h1{font-size:18px;margin:0 0 4px}
      .code{font:700 32px ui-monospace,monospace;letter-spacing:4px;border:2px dashed #0B4DBA;padding:16px;text-align:center;margin:20px 0}
      ol{padding-left:20px;line-height:1.6}small{color:#555}</style></head><body>
      <h1>${esc(code.school_name)}</h1><p>${esc(t('parentAccess.printIntro', { child: code.child_name, defaultValue: 'Follow {{child}} on AppAcademia' }))}</p>
      <div class="code">${esc(code.code)}</div>
      <ol><li>${esc(t('parentAccess.step1', 'Go to app.myappacademia.com/onboard'))}</li>
      <li>${esc(t('parentAccess.step2', 'Choose "Parent", then "My child is at a school"'))}</li>
      <li>${esc(t('parentAccess.step3', 'Enter this code and create your password'))}</li></ol>
      <small>${esc(t('parentAccess.validity', { count: code.uses_left, date: code.expires_at ? new Date(code.expires_at).toLocaleDateString(i18n.language) : '—', defaultValue: 'For {{count}} parent(s), until {{date}}' }))}</small>
      <script>window.onload=()=>window.print()</script></body></html>`)
    w.document.close()
  }

  const invite = async () => {
    const address = email.trim()
    if (!/^\S+@\S+\.\S+$/.test(address)) {
      toast({ title: t('parentAccess.badEmail', 'Enter a valid e-mail address'), variant: 'destructive' })
      return
    }
    setSending(true)
    try {
      await studentService.assignParent(studentId, address)
      setInvited(list => [...list, address])
      setEmail('')
      toast({ title: t('parentAccess.invited', { email: address, defaultValue: 'Invitation sent to {{email}}' }) })
    } catch (e: any) {
      toast({ title: e?.response?.data?.detail || t('parentAccess.inviteFailed', 'The invitation could not be sent'), variant: 'destructive' })
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-dashed border-[#0B4DBA]/50 bg-blue-50/50 p-4 text-center dark:bg-blue-950/20">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{t('parentAccess.codeLabel', 'Parent code')}</p>
        {code ? (
          <>
            <p className="my-2 font-mono text-3xl font-bold tracking-[0.3em] text-[#0B4DBA]" data-testid="parent-code">{code.code}</p>
            <p className="text-xs text-slate-500">
              {t('parentAccess.validity', {
                count: code.uses_left,
                date: code.expires_at ? new Date(code.expires_at).toLocaleDateString(i18n.language) : '—',
                defaultValue: 'For {{count}} parent(s), until {{date}}',
              })}
            </p>
          </>
        ) : (
          <p className="my-3 text-sm text-slate-500">{failed ? t('parentAccess.codeFailed', 'The code could not be created') : '…'}</p>
        )}
      </div>

      <p className="text-sm text-slate-600 dark:text-slate-300">
        {t('parentAccess.howTo', 'Give this code to the parents: they sign up as "Parent", choose "My child is at a school" and enter it. They then see marks, report cards, absences and fees.')}
      </p>

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={copy} disabled={!code}>
          <Copy className="mr-1.5 h-4 w-4" />{t('parentAccess.copy', 'Copy the message')}
        </Button>
        <Button type="button" variant="outline" size="sm" disabled={!code} asChild={!!code}>
          {code ? (
            <a href={`https://wa.me/?text=${encodeURIComponent(message)}`} target="_blank" rel="noreferrer">
              <MessageCircle className="mr-1.5 h-4 w-4" />WhatsApp
            </a>
          ) : (
            <span><MessageCircle className="mr-1.5 h-4 w-4" />WhatsApp</span>
          )}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={print} disabled={!code}>
          <Printer className="mr-1.5 h-4 w-4" />{t('parentAccess.print', 'Print')}
        </Button>
      </div>

      <div className="border-t pt-4">
        <Label htmlFor="parent-invite-email">{t('parentAccess.byEmail', 'Or invite a parent by e-mail')}</Label>
        <div className="mt-1.5 flex gap-2">
          <Input id="parent-invite-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="parent@email.com" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); invite() } }} />
          <Button type="button" onClick={invite} disabled={sending || !email.trim()}>
            <Mail className="mr-1.5 h-4 w-4" />{t('parentAccess.send', 'Send')}
          </Button>
        </div>
        {(linkedEmail || invited.length > 0) && (
          <ul className="mt-2 space-y-1 text-xs text-emerald-700 dark:text-emerald-400">
            {linkedEmail && <li>✓ {t('parentAccess.emailLinked', { email: linkedEmail, defaultValue: '{{email}} is linked to this pupil (invited by e-mail if new)' })}</li>}
            {invited.map(a => <li key={a}>✓ {t('parentAccess.invitedTo', { email: a, defaultValue: 'Invitation sent to {{email}}' })}</li>)}
          </ul>
        )}
      </div>
    </div>
  )
}
